import { useEffect, useState } from 'react'
import { FiPlusCircle } from 'react-icons/fi'
import styled from 'styled-components'
import {
    useActions,
    useTranslation,
    useTypedSelector,
} from '../../../../../hooks'
import { Card, CardHeader } from '../../../../styled/cards'
import {
    ErrorMessage,
    FormField,
    FormInput40,
    PrimaryLabel,
} from '../../../../styled/formElements'
import ButtonSpinnerWhite from '../../../../layout/spinner/ButtonSpinnerWhite'
import { checkEmail, isValidEmail } from '../../../../../helpers/validation'

const InviteButton = styled.div`
    height: 3.5rem;
    padding: 0 1rem;
    background: ${({ theme }) => `${theme.blue}10`};
    border-radius: 0.5rem;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background 0.2s;

    &:hover {
        background: ${({ theme }) => `${theme.blue}20`};
    }
`

const InviteButtonIcon = styled(FiPlusCircle)`
    color: ${({ theme }) => theme.blue};
`

const InviteButtonText = styled.span`
    font-size: 1.2rem;
    color: ${({ theme }) => theme.blue};
    margin-left: 0.5rem;
`

const UserList = styled.div`
    padding: 0;
`

const UserRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 1.4rem 1.8rem;
    border-bottom: 1px solid ${({ theme }) => `${theme.primary}10`};

    &:last-child {
        border-bottom: none;
    }
`

const UserInfo = styled.div`
    display: flex;
    flex-direction: column;
`

const UserName = styled.span`
    font-size: 1.4rem;
    font-weight: 600;
    color: ${({ theme }) => theme.primary};
`

const UserEmail = styled.span`
    font-size: 1.2rem;
    color: ${({ theme }) => `${theme.primary}80`};
    margin-top: 0.2rem;
`

const UserActions = styled.div`
    display: flex;
    align-items: center;
    gap: 1.2rem;
`

const StatusPill = styled.span<{ isPending: boolean }>`
    font-size: 1.1rem;
    font-weight: 600;
    padding: 0.4rem 1rem;
    border-radius: 2rem;
    color: ${({ isPending }) => (isPending ? '#d97706' : '#059669')};
    background: ${({ isPending }) => (isPending ? '#fef3c7' : '#d1fae5')};
`

const RevokeLink = styled.span`
    font-size: 1.2rem;
    color: ${({ theme }) => `${theme.primary}50`};
    cursor: pointer;

    &:hover {
        color: ${({ theme }) => theme.red ?? '#ef4444'};
    }
`

const EmptyText = styled.p`
    font-size: 1.4rem;
    color: ${({ theme }) => `${theme.primary}60`};
    text-align: center;
    padding: 3rem 0;
`

const ModalOverlay = styled.div`
    height: 100vh;
    width: 100%;
    background-color: rgba(0, 0, 0, 0.3);
    position: fixed;
    top: 0;
    left: 0;
    z-index: 500;
`

const ModalCard = styled.div`
    width: 55.6rem;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 501;
`

const ModalContent = styled.div`
    width: 35rem;
    margin: 2rem auto 0 auto;
    padding-bottom: 2.8rem;
`

const CancelButton = styled.p`
    font-size: 1.4rem;
    color: ${({ theme }) => `${theme.primary}50`};
    cursor: pointer;
`

const SubmitButton = styled.div<{ isActive: boolean }>`
    height: 5rem;
    width: 100%;
    background-color: ${({ theme, isActive }) =>
        isActive ? theme.primary : `${theme.primary}50`};
    color: ${({ theme }) => theme.white};
    font-size: 1.6rem;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 0.5rem;
    cursor: ${({ isActive }) => (isActive ? 'pointer' : 'default')};
    margin-top: 2rem;
`

const SuccessMessage = styled.p`
    font-size: 1.2rem;
    color: #059669;
    margin-top: 0.4rem;
`

const PartnerUsers: React.FC = () => {
    const translation = useTranslation()
    const {
        getPartnerUsers,
        invitePartnerUser,
        revokePartnerUser,
        deletePartnerUser,
    } = useActions()
    const { organization } = useTypedSelector(
        (state) => state.caseworker.identity
    )
    const authUser = useTypedSelector((state) => state.auth.user)
    const { caseworkers, fetchCaseworkersLoading } = useTypedSelector(
        (state) => state.caseworker.team
    )

    const visibleUsers = caseworkers.filter((worker: any) => {
        if (worker.id === authUser?.userId) {
            return worker.groups?.includes('contact-line')
        }
        return true
    })

    const [showModal, setShowModal] = useState(false)
    const [email, setEmail] = useState('')
    const [emailError, setEmailError] = useState('')
    const [isInviting, setIsInviting] = useState(false)
    const [successMessage, setSuccessMessage] = useState('')
    const [deleteError, setDeleteError] = useState('')
    const [deleteSuccess, setDeleteSuccess] = useState('')

    useEffect(() => {
        if (organization) {
            getPartnerUsers(organization.id)
        }
        // eslint-disable-next-line
    }, [organization])

    const handleInvite = async () => {
        if (!organization || !isValidEmail(email)) return
        setIsInviting(true)
        setEmailError('')
        setSuccessMessage('')
        const result: any = await invitePartnerUser(organization.id, email, [
            'contact-line',
        ])
        setIsInviting(false)
        if (result) {
            setEmail('')
            setSuccessMessage('Invitation sent!')
            getPartnerUsers(organization.id)
            setTimeout(() => {
                handleCloseModal()
            }, 1200)
        } else {
            setEmailError(
                'Failed to send invitation. The email may already be in use.'
            )
        }
    }

    const handleCloseModal = () => {
        setShowModal(false)
        setEmail('')
        setEmailError('')
        setSuccessMessage('')
    }

    const handleRevoke = async (userId: number) => {
        if (!organization) return
        await revokePartnerUser(organization.id, userId)
        getPartnerUsers(organization.id)
    }

    const handleDelete = async (userId: number) => {
        if (!organization) return
        setDeleteError('')
        setDeleteSuccess('')
        const result: any = await deletePartnerUser(organization.id, userId)
        if (result === true) {
            setDeleteSuccess('User deleted successfully.')
            getPartnerUsers(organization.id)
            setTimeout(() => setDeleteSuccess(''), 3000)
        } else if (result?.error) {
            setDeleteError(result.error)
        } else {
            setDeleteError('Failed to delete user.')
        }
    }

    const getUserDisplayName = (worker: any) => {
        const first = worker.first_name || worker.firstName
        const last = worker.last_name || worker.lastName
        if (first || last) return `${first || ''} ${last || ''}`.trim()
        return null
    }

    const getUserStatus = (worker: any) => {
        const status = worker.status ?? worker.onboarding_status
        if (status === null || status === undefined) return 'Active'
        return 'Pending'
    }

    const emailValidation = () => {
        setEmailError(checkEmail(email, translation))
    }

    return (
        <>
            {showModal && (
                <ModalOverlay>
                    <ModalCard>
                        <Card>
                            <CardHeader>
                                {
                                    translation.inviteCaseworkerModal
                                        .inviteNewCaseworker
                                }
                                <CancelButton onClick={handleCloseModal}>
                                    {translation.reusable.cancel}
                                </CancelButton>
                            </CardHeader>
                            <ModalContent>
                                <FormField>
                                    <PrimaryLabel
                                        isError={!!emailError}
                                        isSuccess={!!successMessage}
                                    >
                                        {translation.reusable.email} *
                                    </PrimaryLabel>
                                    <FormInput40
                                        type="email"
                                        placeholder="Email"
                                        value={email}
                                        onChange={(e) =>
                                            setEmail(e.target.value)
                                        }
                                        isError={emailError.length > 0}
                                        isSuccess={!!successMessage}
                                        onBlur={emailValidation}
                                        onFocus={() => {
                                            setEmailError('')
                                            setSuccessMessage('')
                                        }}
                                    />
                                    {emailError.length > 0 && (
                                        <ErrorMessage>
                                            {emailError}
                                        </ErrorMessage>
                                    )}
                                    {successMessage && (
                                        <SuccessMessage>
                                            {successMessage}
                                        </SuccessMessage>
                                    )}
                                </FormField>
                                <SubmitButton
                                    isActive={isValidEmail(email)}
                                    onClick={handleInvite}
                                >
                                    {isInviting ? (
                                        <ButtonSpinnerWhite />
                                    ) : (
                                        translation.inviteCaseworkerModal
                                            .sendInvitation
                                    )}
                                </SubmitButton>
                            </ModalContent>
                        </Card>
                    </ModalCard>
                </ModalOverlay>
            )}

            <Card>
                <CardHeader>
                    {translation.singleClientViewPartner.lineOfContact}
                    <InviteButton onClick={() => setShowModal(true)}>
                        <InviteButtonIcon size={15} />
                        <InviteButtonText>
                            {
                                translation.inviteCaseworkerModal
                                    .inviteNewCaseworker
                            }
                        </InviteButtonText>
                    </InviteButton>
                </CardHeader>
                {fetchCaseworkersLoading ? (
                    <EmptyText>Loading...</EmptyText>
                ) : visibleUsers.length === 0 ? (
                    <EmptyText>
                        {
                            translation.singleClientViewPartner
                                .lineOfContactsHaveNot
                        }
                    </EmptyText>
                ) : (
                    <UserList>
                        {deleteError && (
                            <div
                                style={{
                                    padding: '1rem 1.8rem',
                                    color: '#ef4444',
                                    fontSize: '1.3rem',
                                }}
                            >
                                {deleteError}
                            </div>
                        )}
                        {deleteSuccess && (
                            <div
                                style={{
                                    padding: '1rem 1.8rem',
                                    color: '#059669',
                                    fontSize: '1.3rem',
                                }}
                            >
                                {deleteSuccess}
                            </div>
                        )}
                        {visibleUsers.map((worker: any) => {
                            const displayName = getUserDisplayName(worker)
                            const status = getUserStatus(worker)
                            const isPending = status === 'Pending'
                            return (
                                <UserRow key={worker.id}>
                                    <UserInfo>
                                        <UserName>
                                            {displayName || worker.email}
                                        </UserName>
                                        {displayName && (
                                            <UserEmail>
                                                {worker.email}
                                            </UserEmail>
                                        )}
                                    </UserInfo>
                                    <UserActions>
                                        <StatusPill isPending={isPending}>
                                            {status}
                                        </StatusPill>
                                        {isPending ? (
                                            <RevokeLink
                                                onClick={() =>
                                                    handleRevoke(worker.id)
                                                }
                                            >
                                                Revoke
                                            </RevokeLink>
                                        ) : (
                                            <RevokeLink
                                                onClick={() =>
                                                    handleDelete(worker.id)
                                                }
                                            >
                                                {translation.deleteText.delete}
                                            </RevokeLink>
                                        )}
                                    </UserActions>
                                </UserRow>
                            )
                        })}
                    </UserList>
                )}
            </Card>
        </>
    )
}

export default PartnerUsers
